import React, { useState } from 'react';

import { errorMessage } from '../../services/apiError';
import axiosInstance from '../../services/axiosInstance';

interface DocUploadChoiceCardProps {
  filename: string;
  jobKey: string;
  projectId: string;
  sessionId?: string | null;
  recommended: 'replace' | 'merge';
  onDone?: () => void;
}

type Mode = 'replace' | 'merge';

const CHOICES: { mode: Mode; label: string; description: string }[] = [
  {
    mode: 'replace',
    label: 'Replace',
    description: 'Forget old context — re-plan everything with this document',
  },
  {
    mode: 'merge',
    label: 'Merge',
    description: 'Blend new document with existing requirements',
  },
];

const DocUploadChoiceCard: React.FC<DocUploadChoiceCardProps> = ({
  filename,
  jobKey,
  projectId,
  sessionId,
  recommended,
  onDone,
}) => {
  const [selected, setSelected] = useState<Mode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitMode = async (mode: Mode) => {
    if (selected || loading) return;
    setLoading(true);
    setSelected(mode);
    setError(null);
    try {
      await axiosInstance.post(`/api/v1/projects/${projectId}/doc-mode`, {
        mode,
        job_key: jobKey,
        session_id: sessionId ?? null,
      });
      // Auto-dismiss after a short delay so the user sees the confirmation
      setTimeout(() => onDone?.(), 1500);
    } catch (err) {
      console.error('Failed to submit doc mode choice:', err);
      setError(errorMessage(err, 'We couldn’t start processing this document. Choose a mode and try again.'));
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="doc-upload-choice-card">
      <div className="doc-upload-choice-header">
        <div>
          <div className="doc-upload-choice-title">New document uploaded</div>
          <div className="doc-upload-choice-filename">&ldquo;{filename}&rdquo;</div>
        </div>
      </div>

      <div className="doc-upload-choice-prompt">How should I handle this?</div>

      <div className="doc-upload-choice-buttons">
        {CHOICES.map(({ mode, label, description }, idx) => {
          const isSelected = selected === mode;
          const isDisabled = selected !== null && !isSelected;
          const isRecommended = mode === recommended;
          const letter = String.fromCharCode(65 + idx);

          return (
            <button
              key={mode}
              id={`doc-choice-${mode}-${projectId}`}
              className={`doc-choice-btn${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}${isRecommended && !selected ? ' recommended' : ''}`}
              onClick={() => submitMode(mode)}
              disabled={isDisabled || loading}
              type="button"
            >
              <span className="doc-choice-letter">[{letter}]</span>
              <div className="doc-choice-text">
                <span className="doc-choice-label">
                  {label}
                  {isRecommended && !selected && (
                    <span className="doc-choice-recommended-badge">Recommended</span>
                  )}
                </span>
                <span className="doc-choice-desc">{description}</span>
              </div>
              {isSelected && loading && <span className="doc-choice-spinner">...</span>}
              {isSelected && !loading && <span className="doc-choice-check">✓</span>}
            </button>
          );
        })}
      </div>


      {selected && !loading && (
        <div className="doc-upload-choice-status">
          Processing document with <strong>{selected}</strong> mode…
        </div>
      )}

      {error && <div className="doc-upload-choice-error">{error}</div>}
    </div>
  );
};

export default DocUploadChoiceCard;
